import { useState } from "react";
import {
  createFileRoute,
  Link,
  useCanGoBack,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, Download, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import { RefreshButton } from "@/components/data/refresh-button";
import { Button } from "@/components/ui/button";
import { useRugLoginValid } from "@/components/rug/rug-login-guard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { DetailField as Field } from "@/components/data/detail-field";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { RegistrationStatusBadge } from "@/components/data/status-badge";
import { bajasApi } from "@/api/deregistrations";
import type { Candidata, Deregistration } from "@/api/deregistrations";
import { queryKeys } from "@/api/query-keys";
import { authErrorMessage } from "@/auth/errors";
import { requireRole } from "@/auth/role-guard";
import { formatDateTime, formatRelative, localeFor } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/bajas/$bajaId/")({
  beforeLoad: requireRole(["admin", "baja"]),
  component: BajaDetailPage,
});

function BajaDetailPage() {
  const { bajaId } = Route.useParams();
  const qc = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Volver por el historial conserva los filtros de la lista (viven en su URL). Un
  // `Link to="/bajas"` navega a la ruta SIN search params, así que tiraba el filtro de
  // estado y el año, y el operador volvía a una lista distinta de la que dejó.
  // Fallback (entrada directa al detalle, sin historial): al listado sin filtros.
  const goBackToList = () =>
    canGoBack ? router.history.back() : navigate({ to: "/bajas" });

  const baja = useQuery({
    queryKey: queryKeys.bajas.detail(bajaId),
    queryFn: () => bajasApi.get(bajaId),
    // El staleTime global (30s) evitaría el refetch al volver a entrar, y el estado
    // de una baja cambia solo (la mueve el bot), así que aquí siempre se revalida.
    refetchOnMount: "always",
  });

  const { isValid: loginValid } = useRugLoginValid("baja");

  const retryMutation = useMutation({
    mutationFn: () => bajasApi.retry(bajaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.bajas.all });
      toast.success(t("bajas.detail.retryToast"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  // Borrado: solo estados que NO cancelaron nada en el RUG. La UI únicamente esconde
  // el botón; la defensa real está en el backend, que re-valida el estado.
  const canDelete = ["failed", "pending", "scheduled", "skipped_duplicate"].includes(
    baja.data?.status ?? "",
  );

  const snapshot = baja.data?.cancellationSnapshot;
  // Lo que el portal tenía registrado manda sobre lo que trajo el layout: el snapshot
  // es la foto de la garantía real al momento de cancelarla.
  const grantorName = snapshot?.otorganteNombre ?? baja.data?.grantorName ?? "";
  const creditorName = snapshot?.acreedorNombre ?? baja.data?.principalCreditor ?? "";
  const hasHighlights = !!(grantorName || creditorName || snapshot?.tipoGarantia);

  const deleteMutation = useMutation({
    mutationFn: () => bajasApi.remove(bajaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.bajas.all });
      // La inscripción de origen pudo quedar liberada, así que también se invalida.
      qc.invalidateQueries({ queryKey: queryKeys.registrations.all });
      toast.success(t("bajas.detail.deleteToast"));
      goBackToList();
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const receiptMutation = useMutation({
    mutationFn: () => bajasApi.receipt(bajaId),
    onSuccess: (info) => {
      window.open(info.url, "_blank", "noopener,noreferrer");
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  return (
    <>
      <button
        type="button"
        onClick={goBackToList}
        className="inline-flex cursor-pointer items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="size-4" />
        {t("bajas.detail.back")}
      </button>

      {baja.isLoading ? (
        <DetailSkeleton />
      ) : baja.isError ? (
        <ErrorBanner error={baja.error} message={t("bajas.detail.loadError")} />
      ) : baja.data ? (
        <>
          <PageHeader
            /* El contrato es opcional (el layout puede traer solo el VIN), y sin
               respaldo el encabezado se quedaba en blanco. Se cae al dato que
               identifique la garantía, que es lo que el operador reconoce. */
            title={
              baja.data.contractNumber ||
              baja.data.assetSerialNumber ||
              baja.data.folioRug ||
              "—"
            }
            subtitle={
              <span className="inline-flex items-center gap-2">
                <RegistrationStatusBadge status={baja.data.status} />
                {/* Programada: sin la fecha, "Programado" no dice nada útil. */}
                {baja.data.scheduledFor && baja.data.status === "scheduled" ? (
                  <span className="text-muted-foreground">
                    {t("bajas.detail.scheduledFor")}{" "}
                    <span className="text-foreground">
                      {new Date(baja.data.scheduledFor).toLocaleString(localeFor(), {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </span>
                  </span>
                ) : baja.data.status === "queued" ? (
                  <span className="text-muted-foreground">
                    {t("bajas.detail.queuedShort")}
                  </span>
                ) : null}
                {baja.data.folioRug ? (
                  <span className="text-muted-foreground">
                    {t("bajas.detail.folioLabel")}{" "}
                    <span className="font-mono text-foreground">
                      {baja.data.folioRug}
                    </span>
                  </span>
                ) : null}
                {/* Baja pedida desde una inscripción: enlace de ida al histórico
                    (el de vuelta ya vive en el detalle de la inscripción). Las bajas
                    de layout no traen vínculo porque pueden no existir aquí. */}
                {baja.data.parentId ? (
                  <Link
                    to="/bajas/$bajaId"
                    params={{ bajaId: baja.data.parentId }}
                    className="text-xs underline text-muted-foreground hover:text-foreground"
                  >
                    {t("bajas.detail.group.viewMother")}
                  </Link>
                ) : null}
                {baja.data.registrationId ? (
                  <Link
                    to="/registrations/$registrationId"
                    params={{ registrationId: baja.data.registrationId }}
                    className="text-xs underline text-muted-foreground hover:text-foreground"
                  >
                    {t("bajas.detail.registrationLink")}
                  </Link>
                ) : null}
              </span>
            }
            actions={
              <div className="flex items-center gap-2">
                <RefreshButton
                  onClick={() => baja.refetch()}
                  spinning={baja.isFetching}
                />
                {baja.data.boletaS3Key ? (
                  <Button
                    variant="outline"
                    disabled={receiptMutation.isPending}
                    onClick={() => receiptMutation.mutate()}
                  >
                    {receiptMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Download className="size-4" />
                    )}
                    {t("bajas.detail.downloadReceipt")}
                  </Button>
                ) : null}
                {baja.data.status === "failed" ? (
                  <Button
                    disabled={retryMutation.isPending || !loginValid}
                    onClick={() => retryMutation.mutate()}
                  >
                    <RefreshCw
                      className={
                        retryMutation.isPending
                          ? "size-4 animate-spin"
                          : "size-4"
                      }
                    />
                    {t("bajas.actions.retry")}
                  </Button>
                ) : null}
                {canDelete ? (
                  <Button
                    variant="outline"
                    className="text-destructive hover:bg-destructive/10"
                    disabled={deleteMutation.isPending}
                    onClick={() => setDeleteOpen(true)}
                  >
                    {deleteMutation.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Trash2 className="size-4" />
                    )}
                    {t("bajas.actions.delete")}
                  </Button>
                ) : null}
              </div>
            }
          />

          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("bajas.detail.deleteTitle")}</DialogTitle>
                <DialogDescription>
                  {t("bajas.detail.deleteConfirm")}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" disabled={deleteMutation.isPending}>
                    {t("common.actions.cancel")}
                  </Button>
                </DialogClose>
                <Button
                  variant="destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate()}
                >
                  {deleteMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Trash2 className="size-4" />
                  )}
                  {t("bajas.actions.delete")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Una sola sección, con la misma anatomía que el detalle de inscripción:
              fila destacada + bien a ancho completo + separador + rejilla de campos.
              Se pinta SIEMPRE, incluso sin snapshot (baja fallida o en cola): los
              campos se ocultan solos, así que la tarjeta nunca queda hueca. */}
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t("bajas.detail.info.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Una baja de layout en cola no trae nada de esto todavía, y una fila
                  de tres guiones se ve peor que no tenerla. Con al menos un dato sí
                  se pinta completa, para que las columnas sigan cuadrando. */}
              {hasHighlights ? (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">
                      {t("bajas.detail.info.grantor")}
                    </p>
                    <p className="font-medium truncate" title={grantorName}>
                      {grantorName || "—"}
                    </p>
                    {snapshot?.otorganteTipoPersona ? (
                      <p className="text-xs text-muted-foreground">
                        {snapshot.otorganteTipoPersona}
                      </p>
                    ) : null}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">
                      {t("bajas.detail.info.creditor")}
                    </p>
                    <p className="font-medium truncate" title={creditorName}>
                      {creditorName || "—"}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">
                      {t("bajas.detail.info.guaranteeType")}
                    </p>
                    <p className="font-medium">{snapshot?.tipoGarantia || "—"}</p>
                  </div>
                </div>
              ) : null}

              {snapshot?.descripcionBien ? (
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t("bajas.detail.info.asset")}
                  </p>
                  <p className="text-sm text-foreground">{snapshot.descripcionBien}</p>
                </div>
              ) : null}

              {/* Sin nada arriba, el separador quedaría flotando sobre la rejilla. */}
              {hasHighlights || snapshot?.descripcionBien ? <Separator /> : null}

              <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
                <Field
                  label={t("bajas.detail.info.guaranteeNumber")}
                  value={snapshot?.garantiaNumero}
                />
                <Field
                  label={t("bajas.detail.info.serial")}
                  value={baja.data.assetSerialNumber}
                  mono
                />
                <Field
                  label={t("bajas.detail.info.electronicFolio")}
                  value={snapshot?.otorganteFolioElectronico}
                  mono
                />
                <Field
                  label={t("bajas.detail.info.debtor")}
                  value={snapshot?.deudorNombre}
                />
                <Field label={t("bajas.detail.info.term")} value={snapshot?.vigencia} />
                <Field
                  label={t("bajas.detail.info.signingDate")}
                  value={snapshot?.fechaCelebracion}
                />
                <Field
                  label={t("bajas.detail.info.terminationDate")}
                  value={snapshot?.fechaTerminacion}
                />
                <Field
                  label={t("bajas.detail.info.maxAmount")}
                  value={snapshot?.montoMaximo}
                />
                <Field
                  label={t("bajas.detail.info.assetType")}
                  value={snapshot?.tipoBien}
                />
                <Field
                  label={t("bajas.detail.info.reason")}
                  value={baja.data.cancellationReason}
                />
                <Field
                  label={t("bajas.detail.info.attempts")}
                  value={String(baja.data.attempts ?? 0)}
                />
                <Field
                  label={t("bajas.detail.info.updatedAt")}
                  value={formatDateTime(baja.data.updatedAt)}
                />
                {/* `formatDateTime` devuelve "—" con undefined, y ese guion sí llenaría
                    la celda. Se condiciona antes para que el campo se oculte. */}
                <Field
                  label={t("bajas.detail.info.processedAt")}
                  value={
                    baja.data.processedAt
                      ? formatDateTime(baja.data.processedAt)
                      : undefined
                  }
                />
              </dl>

              {/* `snapshot.rawText` no se pinta a propósito; el porqué está en su
                  declaración, en `api/deregistrations.ts`. */}

              {snapshot?.capturedAt ? (
                <p className="text-xs text-muted-foreground">
                  {t("bajas.detail.info.capturedAt")}{" "}
                  {formatRelative(snapshot.capturedAt)}
                </p>
              ) : null}
            </CardContent>
          </Card>

          {/* GRUPO madre + hijas y GARANTÍAS ENCONTRADAS: solo cuando el VIN tenía varias
              garantías (spec-baja-limpia-todas-las-garantias). Una baja normal no las pinta. */}
          {baja.data.grupo ? <GrupoCard actual={baja.data} grupo={baja.data.grupo} /> : null}
          {baja.data.candidatas?.length ? <CandidatasCard candidatas={baja.data.candidatas} /> : null}

          {baja.data.errorMessage ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive flex items-start gap-3 mb-6">
              <AlertCircle className="size-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">{t("bajas.detail.lastError")}</p>
                <p className="text-xs mt-0.5 text-destructive/80">
                  {baja.data.errorMessage}
                </p>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/**
 * Madre e hijas de un grupo de cancelaciones. La fila de la baja abierta se marca; las
 * demás enlazan a su detalle. El estado de cada una es el de siempre: una hija tiene su
 * propia firma, verificación y comprobante.
 */
function GrupoCard({
  actual,
  grupo,
}: {
  actual: Deregistration;
  grupo: NonNullable<Deregistration["grupo"]>;
}) {
  const { t } = useTranslation();
  const filas: { baja: Deregistration; rol: "mother" | "child" }[] = [
    { baja: grupo.madre, rol: "mother" },
    ...grupo.hijas.map((h) => ({ baja: h, rol: "child" as const })),
  ];
  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {t("bajas.detail.group.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">{t("bajas.detail.group.intro")}</p>
        <ul className="divide-y rounded-md border">
          {filas.map(({ baja, rol }) => (
            <li key={baja.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 text-sm">
              <span className="w-28 shrink-0 text-xs text-muted-foreground">
                {rol === "mother" ? t("bajas.detail.group.mother") : t("bajas.detail.group.child")}
              </span>
              <span className="font-mono">
                {t("bajas.detail.group.guarantee")} {baja.numeroGarantia ?? baja.folioRug ?? "—"}
              </span>
              <RegistrationStatusBadge status={baja.status} />
              {baja.id === actual.id ? (
                <span className="text-xs text-muted-foreground">({t("bajas.detail.group.thisOne")})</span>
              ) : (
                <Link
                  to="/bajas/$bajaId"
                  params={{ bajaId: baja.id }}
                  className="ml-auto text-xs underline text-muted-foreground hover:text-foreground"
                >
                  {t("bajas.detail.group.open")}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/** Todas las garantías que la búsqueda por VIN devolvió, con el desenlace de cada una. */
function CandidatasCard({ candidatas }: { candidatas: Candidata[] }) {
  const { t } = useTranslation();
  return (
    <Card className="mb-6">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {t("bajas.detail.candidates.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">{t("bajas.detail.candidates.intro")}</p>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">{t("bajas.detail.candidates.guarantee")}</th>
                <th className="px-3 py-2 text-left font-medium">{t("bajas.detail.candidates.date")}</th>
                <th className="px-3 py-2 text-left font-medium">{t("bajas.detail.candidates.grantor")}</th>
                <th className="px-3 py-2 text-left font-medium">{t("bajas.detail.candidates.creditor")}</th>
                <th className="px-3 py-2 text-left font-medium">{t("bajas.detail.candidates.outcome")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {candidatas.map((c) => (
                <tr key={c.idGarantia}>
                  <td className="px-3 py-2 font-mono">{c.idGarantia}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{c.fechaInscripcion ?? "—"}</td>
                  <td className="px-3 py-2">{c.otorganteNombre ?? "—"}</td>
                  <td className="px-3 py-2">{c.acreedorNombre ?? "—"}</td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{t(`bajas.detail.candidates.outcomes.${c.desenlace}`)}</span>
                    {c.motivo ? (
                      <span className="block text-xs text-muted-foreground" title={c.motivo}>
                        {c.motivo}
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function DetailSkeleton() {
  return (
    <>
      <Skeleton className="h-8 w-72 mb-2" />
      <Skeleton className="h-4 w-48 mb-6" />
      {/* Una sola tarjeta, igual que el layout real, para que al cargar no brinque. */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <Skeleton className="h-3 w-32" />
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-4 w-full" />
              </div>
            ))}
          </div>
          <Skeleton className="h-4 w-3/4" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="space-y-1.5">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function ErrorBanner({ error, message }: { error: unknown; message: string }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive flex items-start gap-3">
      <AlertCircle className="size-4 mt-0.5 shrink-0" />
      <div>
        <p className="font-medium">{message}</p>
        <p className="text-xs mt-0.5 text-destructive/80">
          {error instanceof Error ? error.message : t("common.error.retryHint")}
        </p>
      </div>
    </div>
  );
}
